import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

/**
 * One modal that handles all four manual stock-movement variants
 * (receive / issue / transfer / adjust) by switching its `kind` prop.
 * The relevant fields and the API call adapt to the kind.
 */
export function StockMovementModal({ kind, open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [delta, setDelta] = useState('1');
  const [unitCost, setUnitCost] = useState('0');
  const [notes, setNotes] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [serials, setSerials] = useState('');
  const [error, setError] = useState(null);

  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items || []),
    enabled: open,
  });
  const warehouses = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => inventoryApi.listWarehouses().then((r) => r.items || []),
    enabled: open,
  });

  function reset() {
    setProductId('');
    setWarehouseId('');
    setToWarehouseId('');
    setQuantity('1');
    setDelta('1');
    setUnitCost('0');
    setNotes('');
    setBatchNumber('');
    setExpiryDate('');
    setSerials('');
    setError(null);
  }

  const selectedProduct = (products.data || []).find((p) => p.id === productId);
  const tracking = selectedProduct?.tracking || 'NONE';

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'low-stock'] });
  };

  const submit = useMutation({
    mutationFn: async () => {
      if (kind === 'receive') {
        const body = {
          productId, warehouseId,
          quantity: Number(quantity),
          unitCost: Number(unitCost),
          notes: notes || undefined,
        };
        if (tracking === 'BATCH') {
          body.batchNumber = batchNumber;
          if (expiryDate) body.expiryDate = expiryDate;
        } else if (tracking === 'SERIAL') {
          body.serialNumbers = serials.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        }
        return inventoryApi.receiveStock(body);
      }
      if (kind === 'issue') {
        return inventoryApi.issueStock({
          productId, warehouseId,
          quantity: Number(quantity),
          notes: notes || undefined,
        });
      }
      if (kind === 'transfer') {
        return inventoryApi.transferStock({
          productId,
          fromWarehouseId: warehouseId,
          toWarehouseId,
          quantity: Number(quantity),
          notes: notes || undefined,
        });
      }
      if (kind === 'adjust') {
        const deltaNumber = Number(delta);
        return inventoryApi.adjustStock({
          productId, warehouseId,
          delta: deltaNumber,
          unitCost: deltaNumber > 0 ? Number(unitCost) : undefined,
          notes: notes || undefined,
        });
      }
      throw new Error(`Unknown movement kind: ${kind}`);
    },
    onSuccess: () => {
      invalidateAll();
      reset();
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  const TITLES = {
    receive: t('inv.movements.receive.title'),
    issue: t('inv.movements.issue.title'),
    transfer: t('inv.movements.transfer.title'),
    adjust: t('inv.movements.adjust.title'),
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={TITLES[kind] || ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submit.isPending}>
            {submit.isPending ? `${t('common.save')}…` : t('common.confirm')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {kind === 'adjust' && (
          <p className="muted text-sm" style={{ margin: 0 }}>{t('inv.movements.adjust.note')}</p>
        )}

        <label className="field">
          <span className="field-label">{t('inv.movements.modal.product')}</span>
          <select className="select" value={productId} onChange={(e) => setProductId(e.target.value)} required>
            <option value="">{t('inv.po.modal.pickProduct')}</option>
            {(products.data || []).map((p) => (
              <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
            ))}
          </select>
        </label>

        {kind === 'transfer' ? (
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('inv.movements.modal.fromWarehouse')}</span>
              <select className="select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(warehouses.data || []).map((w) => (
                  <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t('inv.movements.modal.toWarehouse')}</span>
              <select className="select" value={toWarehouseId} onChange={(e) => setToWarehouseId(e.target.value)} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(warehouses.data || [])
                  .filter((w) => w.id !== warehouseId)
                  .map((w) => (
                    <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
                  ))}
              </select>
            </label>
          </div>
        ) : (
          <label className="field">
            <span className="field-label">{t('inv.movements.modal.warehouse')}</span>
            <select className="select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
              {(warehouses.data || []).map((w) => (
                <option key={w.id} value={w.id}>{w.code} - {w.name}</option>
              ))}
            </select>
          </label>
        )}

        {kind === 'adjust' ? (
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('inv.movements.modal.delta')}</span>
              <input
                className="input"
                type="number"
                step="any"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                required
              />
            </label>
            {Number(delta) > 0 && (
              <label className="field">
                <span className="field-label">{t('inv.movements.modal.unitCost')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  required
                />
              </label>
            )}
          </div>
        ) : (
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('inv.movements.modal.quantity')}</span>
              <input
                className="input"
                type="number"
                min="0.0001"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
            </label>
            {kind === 'receive' && (
              <label className="field">
                <span className="field-label">{t('inv.movements.modal.unitCost')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  required
                />
              </label>
            )}
          </div>
        )}

        {kind === 'receive' && tracking === 'BATCH' && (
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('inv.track.batchNumber')}</span>
              <input className="input" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field-label">{t('inv.track.expiryDate')}</span>
              <input className="input" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </label>
          </div>
        )}
        {kind === 'receive' && tracking === 'SERIAL' && (
          <label className="field">
            <span className="field-label">{t('inv.track.serialNumbers')}</span>
            <textarea className="input" rows={3} value={serials} onChange={(e) => setSerials(e.target.value)}
              placeholder={t('inv.track.serialHint')} />
          </label>
        )}

        <label className="field">
          <span className="field-label">{t('inv.movements.modal.notes')}</span>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
